import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import { usePopup } from "@/context/PopupContext";
import { getSiteConfig } from "@/config/siteConfigs";
import styles from "./PopupMenu.module.scss";

const PopupMenu: React.FC = () => {
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const popupRef = useRef<HTMLDivElement>(null);
  const { isOpenMenu, closeMenu } = usePopup();

  useEffect(() => {
    document.body.style.overflow = isOpenMenu ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpenMenu]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaToken) {
      alert(t("popupMenu.errors.recaptcha"));
      return;
    }

    setLoading(true);

    const { name, email, phone, message } = formData;

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackform",
      legid: 1,
      language: siteConfig.language,
      data: {
        name,
        phone,
        email,
        message,
        recaptchaToken: captchaToken,
      },
    };

    try {
      await sendToCRM(formPayload);

      (window as any).dataLayer?.push({
        event: "form_submit",
        form_name: "popup_contact_form",
        form_type: "feedbackform",
      });

      alert(t("popupMenu.success"));

      setFormData({ name: "", phone: "", email: "", message: "" });
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
      closeMenu();
    } catch {
      alert(t("popupMenu.error"));
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: data.legid,
        language: siteConfig.language,
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data),
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <>
      {isOpenMenu && (
        <div className={styles.popWrapper} ref={popupRef}>
          <div className="container">
            <div className={styles.popupForm}>
              <button
                className={styles.closePopup}
                onClick={closeMenu}
                aria-label={t("common.close")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    fill="#202428"
                    fillRule="evenodd"
                    d="M.293.293a1 1 0 0 1 1.414 0l30 30a1 1 0 0 1-1.414 1.414l-30-30a1 1 0 0 1 0-1.414Zm31.414 0a1 1 0 0 1 0 1.414l-12 12a1 1 0 0 1-1.414-1.414l12-12a1 1 0 0 1 1.414 0Zm-18 18a1 1 0 0 1 0 1.414l-12 12a1 1 0 0 1-1.414-1.414l12-12a1 1 0 0 1 1.414 0Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <h3 className={styles.popupFormTitle}>{t("popupMenu.title")}</h3>

              <form className={styles.feedbackForm} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>
                    <div className={styles.labelTitle}>
                      {t("popupMenu.fields.name")}
                    </div>
                    <input
                      type="text"
                      name="name"
                      placeholder={t("popupMenu.fields.namePlaceholder")}
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={styles.input}
                    />
                  </label>

                  <label>
                    <div className={styles.labelTitle}>
                      {t("popupMenu.fields.phone")}
                    </div>
                    <input
                      className={styles.input}
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder={t("popupMenu.fields.phonePlaceholder")}
                    />
                  </label>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <div className={styles.labelTitle}>
                      {t("popupMenu.fields.email")}
                    </div>
                    <input
                      className={styles.input}
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t("popupMenu.fields.emailPlaceholder")}
                    />
                  </label>

                  <label htmlFor="file-input">
                    <div className={styles.labelTitle}>
                      {t("popupMenu.fields.attachFile")}
                    </div>
                    <input
                      className={styles.fileInput}
                      type="file"
                      id="file-input"
                    />
                    <span className={styles.fileLabel}>
                      {t("popupMenu.fields.selectFile")}
                    </span>
                  </label>
                </div>

                <div className={styles.textBlock}>
                  <label>
                    <div className={styles.labelTitle}>
                      {t("popupMenu.fields.message")}
                    </div>
                    <textarea
                      placeholder={t("popupMenu.fields.messagePlaceholder")}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </div>

                <div className={styles.checkboxWrapper}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    id="agreement"
                    required
                  />
                  <label htmlFor="agreement">
                    {t("popupMenu.consent")}{" "}
                    <a href="/privacy">{t("popupMenu.consentLink")}</a>
                  </label>
                </div>

                <div className={styles.formGroup}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={siteConfig.recaptcha.siteKey}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </div>

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading || !captchaToken}
                >
                  {loading ? t("popupMenu.submitting") : t("popupMenu.submit")}
                </button>
              </form>

              <div className={styles.menuOptions}>
                <a
                  href={`tel:${siteConfig.contact.phone}`}
                  className={styles.link}
                >
                  <button className={styles.menuOption}>
                    <svg
                      width="29"
                      height="28"
                      viewBox="0 0 29 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.35676 5.63869C6.57548 5.4203 6.8381 5.25089 7.12724 5.14168C7.41637 5.03247 7.72541 4.98595 8.03389 5.00522C8.34236 5.02448 8.64321 5.10909 8.91651 5.25342C9.18981 5.39776 9.42932 5.59853 9.61916 5.84243L11.8628 8.72492C12.2741 9.25366 12.4191 9.94241 12.2566 10.5924L11.5729 13.3299C11.5375 13.4717 11.5394 13.6202 11.5784 13.761C11.6174 13.9019 11.6921 14.0302 11.7953 14.1336L14.8665 17.2049C14.97 17.3083 15.0986 17.3832 15.2397 17.4222C15.3807 17.4612 15.5295 17.4629 15.6715 17.4274L18.4076 16.7436C18.7284 16.6634 19.0632 16.6572 19.3867 16.7254C19.7102 16.7936 20.014 16.9345 20.2751 17.1374L23.1575 19.3799C24.1937 20.1861 24.2887 21.7173 23.3612 22.6436L22.0688 23.9361C21.1438 24.8611 19.7613 25.2673 18.4726 24.8136C15.1742 23.653 12.1794 21.7646 9.71041 19.2886C7.2346 16.8199 5.34631 13.8255 4.18558 10.5274C3.7331 9.23991 4.13933 7.85617 5.06431 6.93118L6.35676 5.63869Z"
                        fill="#1F5459"
                      />
                    </svg>
                    {siteConfig.contact.phone}
                  </button>
                </a>

                <a
                  href={siteConfig.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  <button className={styles.menuOption}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="29"
                      height="28"
                      fill="none"
                      aria-hidden="true"
                    >
                      <g clipPath="url(#a)">
                        <path
                          fill="#1F5459"
                          d="M24.438 4.025C21.798 1.4 18.454 0 14.581 0 7.013 0 .676 6.3.676 13.825c0 2.45.704 4.9 1.936 7L.5 28l7.393-1.925c2.112 1.05 4.4 1.75 6.688 1.75 7.745 0 13.906-6.3 13.906-13.825.176-3.85-1.408-7.35-4.049-9.975ZM21.446 18.9c-.352.875-1.76 1.575-2.288 1.575-.528.175-2.288 0-4.225-.875-3.52-1.575-5.808-5.075-5.984-5.25-.176-.175-1.408-1.925-1.408-3.5s.88-2.625 1.232-2.975c.704-.7 2.112-.525 2.464.175.176.7.88 2.45 1.056 2.625.176.175.176.875-.352 1.225-.176 0-1.056.7-.704 1.225.176.35.88 1.4 1.936 2.45 1.408 1.225 2.464 1.575 2.816 1.75.352.175.528.175.705-.175.176-.175.88-1.05 1.056-1.4.176-.35.528-.35.704-.175.176.175 2.112.875 2.464 1.05.352.35.704.35.704.525.176.175.176.875-.176 1.75Z"
                        />
                      </g>
                      <defs>
                        <clipPath id="a">
                          <path fill="#fff" d="M.5 0h28v28H.5z" />
                        </clipPath>
                      </defs>
                    </svg>
                    {t("popupMenu.actions.whatsapp")}
                  </button>
                </a>

                <a
                  href="/ReparaturAuftrag.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  <button className={styles.menuOption}>
                    <svg
                      width="29"
                      height="28"
                      viewBox="0 0 29 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M16.9395 2H9C8.20435 2 7.44129 2.3029 6.87868 2.84207C6.31607 3.38124 6 4.1125 6 4.875V22.125C6 22.8875 6.31607 23.6188 6.87868 24.1579C7.44129 24.6971 8.20435 25 9 25H21C21.7956 25 22.5587 24.6971 23.1213 24.1579C23.6839 23.6188 24 22.8875 24 22.125V8.76631C23.9999 8.38509 23.8418 8.01952 23.5605 7.75L18 2.42119C17.7188 2.15159 17.3373 2.00008 16.9395 2V2ZM17.25 7.03125V4.15625L21.75 8.46875H18.75C18.3522 8.46875 17.9706 8.3173 17.6893 8.04772C17.408 7.77813 17.25 7.4125 17.25 7.03125ZM7.5 14.9375H22.5V16.375H12V19.25H22.5V20.6875H12V23.5625H10.5V20.6875H7.5V19.25H10.5V16.375H7.5V14.9375Z"
                        fill="#1F5459"
                      />
                    </svg>
                    {t("popupMenu.actions.downloadForm")}
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PopupMenu;
